import LinkIcon from "@mui/icons-material/Link";
import { Button, Dialog, DialogActions, DialogContent, IconButton, styled, Typography } from "@mui/material";
import { Link } from "react-router-dom";
import DialogTitle from "../../componentLibrary/DialogTitle";
import { getNavOrgAthleteWithTeamAnthropometry, getNavOrgAthleteWithTeamSummary } from "../../pages/Auth/Org/Athlete/AthletePaths";
import { useTranslation } from "react-i18next";

const Row = styled("div")(({ theme: { spacing } }) => ({
  display: "flex",
  alignItems: "center",
  paddingTop: 6,
}));

interface MacroProtocolMissingDataDialogProps {
  athleteId: string;
  teamId: string;
  noAnthropometry: boolean;
  noBirthdate: boolean;
  invalidAge: boolean;
  incompleteAnthropometry: boolean;
  missingSex?: boolean;
  // Only used in copy meal plan case, as goal will be created during create meal plan process
  noGoal?: boolean;
  open: boolean;
  onClose: () => void;
  onContinue: () => void;
}

// TODO - This should get reworked to use the shared-utils/macro-protocol lib validation and/or other zod validation
const MacroProtocolMissingDataDialog = ({
  athleteId,
  teamId,
  noAnthropometry,
  noBirthdate,
  invalidAge,
  incompleteAnthropometry,
  missingSex = false,
  noGoal = false,
  open,
  onClose,
  onContinue,
}: MacroProtocolMissingDataDialogProps) => {
  const { t } = useTranslation();
  const anthroUrl = getNavOrgAthleteWithTeamAnthropometry(athleteId, teamId);
  const summaryUrl = getNavOrgAthleteWithTeamSummary(athleteId, teamId);

  const noAnthropometryComponent = (
    <>
      <DialogContent sx={{ flexDirection: "row", gap: 2 }}>
        <Link to={anthroUrl}>
          <IconButton size="large">
            <LinkIcon />
          </IconButton>
        </Link>
        <Typography>{t("macroProtocol.missingDataDialog.noAnthropometry")}</Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>OK</Button>
      </DialogActions>
    </>
  );

  const noGoalComponent = (
    <>
      <DialogContent sx={{ flexDirection: "row", gap: 2 }}>
        <Link to={summaryUrl}>
          <IconButton size="large">
            <LinkIcon />
          </IconButton>
        </Link>
        <Typography>{t("macroProtocol.missingDataDialog.noGoal")}</Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>OK</Button>
      </DialogActions>
    </>
  );

  const incompleteDataComponent = (
    <>
      <DialogContent>
        <Typography>{t("macroProtocol.missingDataDialog.incompleteData")}</Typography>
        {noBirthdate && (
          <Row>
            <Link to={summaryUrl}>
              <IconButton size="large">
                <LinkIcon />
              </IconButton>
            </Link>
            <Typography variant="body2">{t("macroProtocol.missingDataDialog.noBirthdate")}</Typography>
          </Row>
        )}
        {invalidAge && (
          <Row>
            <Link to={summaryUrl}>
              <IconButton size="large">
                <LinkIcon />
              </IconButton>
            </Link>
            <Typography variant="body2">{t("macroProtocol.missingDataDialog.invalidAge")}</Typography>
          </Row>
        )}
        {incompleteAnthropometry && (
          <Row>
            <Link to={anthroUrl}>
              <IconButton size="large">
                <LinkIcon />
              </IconButton>
            </Link>
            <Typography variant="body2">
              {t("macroProtocol.missingDataDialog.incompleteAnthropometry")}
            </Typography>
          </Row>
        )}
        {missingSex && (
          <Row>
            <Link to={summaryUrl}>
              <IconButton size="large">
                <LinkIcon />
              </IconButton>
            </Link>
            <Typography variant="body2">{t("macroProtocol.missingDataDialog.missingGender")}</Typography>
          </Row>
        )}
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={onClose}>
          Back
        </Button>
        <Button autoFocus onClick={onContinue}>
          Continue
        </Button>
      </DialogActions>
    </>
  );

  const childComponent = noAnthropometry ? noAnthropometryComponent : noGoal ? noGoalComponent : incompleteDataComponent;

  return (
    <Dialog
      maxWidth="sm"
      fullWidth
      open={open}
      onClose={onClose}>
      <DialogTitle title={t("macroProtocol.missingDataDialog.title")} onClose={onClose} />
      {childComponent}
    </Dialog>
  );
};

export default MacroProtocolMissingDataDialog;
